import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { Schedule, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { medicosAPI, centrosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import EstudiosCanalesHorario from './EstudiosCanalesHorario';
import { nombreCanal } from '../hooks/useOpcionesCapacidad';
import './grilla-horarios-medicos.css';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;
const DIAS = [
  { v: 0, l: 'Do' }, { v: 1, l: 'Lu' }, { v: 2, l: 'Ma' },
  { v: 3, l: 'Mi' }, { v: 4, l: 'Ju' }, { v: 5, l: 'Vi' }, { v: 6, l: 'Sa' },
];
// Nombres completos de los días (0=Domingo..6=Sábado) para edición y listado
const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const EMPTY = {
  tipoMedico: '1', idMedico: '', idCentro: '',
  horaDesde: '08', minutosDesde: '00',
  dias: [], observacion: '',
  // Solo técnicos: estudios con su canal y turnos por día.
  estudios: [],
};

// Resumen para la grilla: un estudio por línea.
// "PSG C/OXI AMB: 2 turnos por día · Canal 4, Canal 21"
const resumenCapacidad = (h) => {
  const estudios = h.estudios || [];
  if (estudios.length === 0) return '—';
  return (
    <ul className="lista-estudios-horario">
      {estudios.map(e => {
        const canales = (e.canales || []).map(nombreCanal).join(', ');
        const turnos = `${e.maxTurnosDiarios} ${e.maxTurnosDiarios === 1 ? 'turno' : 'turnos'} por día`;
        return (
          <li key={e.idTipoEstudio}>
            <strong>{e.tipoEstudio || `Estudio ${e.idTipoEstudio}`}</strong>: {turnos}
            {canales && <span className="lista-estudios-canales"> · {canales}</span>}
          </li>
        );
      })}
    </ul>
  );
};

// Mensaje del backend (400) o uno genérico.
const mensajeError = (err, porDefecto) => err?.response?.data?.mensaje || porDefecto;

const GrillaHorariosMedicos = () => {
  const [horarios, setHorarios] = useState([]);
  const [centros, setCentros] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState({ idCentro: '' });
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  const { sortedData, requestSort, getSortIcon } = useSortableTable(horarios);

  const cargarMedicos = useCallback(async (tipo) => {
    try {
      const res = tipo === '1' ? await medicosAPI.getMedicos() : await medicosAPI.getTecnicos();
      setMedicos(res.data || []);
    } catch { setMedicos([]); }
  }, []);

  useEffect(() => {
    centrosAPI.getCentros().then(r => setCentros(r.data || [])).catch(() => {});
    cargarMedicos('1');
  }, [cargarMedicos]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await medicosAPI.listarHorariosMedicos(filtros.idCentro || undefined);
      setHorarios(res.data || []);
      setPage(1);
    } catch {
      setToast({ message: 'Error al cargar los horarios.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const paginados = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleNuevo = () => { setEditando(null); setForm(EMPTY); setMostrarForm(true); };

  const handleEditar = async (h) => {
    try {
      const res = await medicosAPI.obtenerHorarioMedico(h.idHorarioMedico);
      const d = res.data;
      await cargarMedicos(String(d.tipoMedico));
      // horaDesde llega como "HH:mm" (o null si es un registro legacy)
      const [horaEd, minEd] = (d.horaDesde || '08:00').split(':');
      setEditando(d);
      setForm({
        tipoMedico: String(d.tipoMedico),
        idMedico: String(d.idMedico),
        idCentro: String(d.idCentro),
        horaDesde: horaEd || '08',
        minutosDesde: minEd || '00',
        // en edición se maneja un único día de la semana
        dias: d.diaSemana != null ? [Number(d.diaSemana)] : [],
        observacion: d.observacion || '',
        estudios: (d.estudios || []).map(e => ({
          idTipoEstudio: e.idTipoEstudio,
          maxTurnosDiarios: e.maxTurnosDiarios,
          canales: (e.canales || []).map(c => ({ canales: c.canales, conCpap: !!c.conCpap })),
        })),
      });
      setMostrarForm(true);
    } catch {
      setToast({ message: 'Error al cargar el horario.', type: 'error' });
    }
  };

  const handleCancelar = () => { setMostrarForm(false); setEditando(null); setForm(EMPTY); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    // Sin centro o sin profesional ⇒ impedir guardado (alta y edición)
    if (!form.idCentro || !form.idMedico) {
      setToast({ message: 'Seleccioná un centro y un profesional.', type: 'error' });
      return;
    }
    if (!editando && form.dias.length === 0) {
      setToast({ message: 'Seleccioná al menos un día de la semana.', type: 'error' });
      return;
    }
    const esTecnico = form.tipoMedico === '2';
    if (esTecnico && form.estudios.some(e => e.maxTurnosDiarios === '')) {
      setToast({ message: 'Completá los turnos por día de cada estudio marcado.', type: 'error' });
      return;
    }
    // Solo técnicos llevan estudios; cada uno con su canal (el backend valida canal y equipos).
    const capacidad = esTecnico
      ? {
        estudios: form.estudios.map(e => ({
          idTipoEstudio: e.idTipoEstudio,
          maxTurnosDiarios: Number(e.maxTurnosDiarios),
          canales: (e.canales || []).map(c => ({ canales: c.canales, conCpap: !!c.conCpap })),
        })),
      }
      : {};
    setLoading(true);
    try {
      if (editando) {
        await medicosAPI.actualizarHorarioMedico({
          idHorarioMedico: editando.idHorarioMedico,
          idMedico: Number(form.idMedico),
          idCentro: Number(form.idCentro),
          diaSemana: Number(form.dias[0]),
          horaDesde: form.horaDesde,
          minutosDesde: form.minutosDesde,
          observacion: form.observacion,
          ...capacidad,
        });
        setToast({ message: 'Horario actualizado con éxito.', type: 'success' });
      } else {
        await medicosAPI.agregarHorariosMedicos({
          idMedico: Number(form.idMedico),
          idCentro: Number(form.idCentro),
          tipoMedico: form.tipoMedico,
          horaDesde: form.horaDesde,
          minutosDesde: form.minutosDesde,
          dias: form.dias,
          observacion: form.observacion,
          ...capacidad,
        });
        setToast({ message: 'Horarios creados con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch (err) {
      setToast({ message: mensajeError(err, 'Error al guardar el horario.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await medicosAPI.eliminarHorarioMedico(confirmEliminar);
      setToast({ message: 'Horario eliminado con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar el horario.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const toggleDia = (v) => setForm(f => ({
    ...f, dias: f.dias.includes(v) ? f.dias.filter(d => d !== v) : [...f.dias, v]
  }));

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Schedule sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Horarios de Médicos y Técnicos
          </h2>
          <span className="page-subtitle">Administración de horarios</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar} size="xl" dialogClassName="modal-horarios-medicos">
          <style>{`
            .modal-horarios-medicos .modal-content { max-width: none !important; width: 100% !important; }
            .modal-horarios-medicos { max-width: 860px !important; width: 90vw !important; }
          `}</style>
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Horario' : 'Nuevo Horario'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body>
              <div className="row">
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>* Centro</Form.Label>
                  <Form.Select key={`c-${form.idCentro}`} value={form.idCentro} onChange={f('idCentro')} required>
                    <option value="">Seleccione...</option>
                    {centros.map(c => <option key={c.idCentro} value={c.idCentro}>{c.centro}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>* Tipo</Form.Label>
                  <div className="d-flex gap-3 mt-1">
                    <Form.Check type="radio" label="Médico" value="1" checked={form.tipoMedico === '1'}
                      onChange={(e) => { setForm(p => ({ ...p, tipoMedico: '1', idMedico: '', estudios: [] })); cargarMedicos('1'); }} />
                    <Form.Check type="radio" label="Técnico" value="2" checked={form.tipoMedico === '2'}
                      onChange={(e) => { setForm(p => ({ ...p, tipoMedico: '2', idMedico: '' })); cargarMedicos('2'); }} />
                  </div>
                </Form.Group>
                <Form.Group className="col-md-12 mb-3">
                  <Form.Label>* Médico / Técnico</Form.Label>
                  <Form.Select key={`m-${form.idMedico}-${form.tipoMedico}`} value={form.idMedico} onChange={f('idMedico')} required>
                    <option value="">Seleccione...</option>
                    {medicos.map(m => <option key={m.idMedico} value={m.idMedico}>{m.medico1}</option>)}
                  </Form.Select>
                </Form.Group>
                {/* Hora desde (único campo de hora) */}
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label htmlFor="horaDesde-input">* Hora desde</Form.Label>
                  <Form.Control id="horaDesde-input" type="time" aria-label="Hora desde"
                    value={`${form.horaDesde}:${form.minutosDesde}`}
                    onChange={e => { const [h, m] = e.target.value.split(':'); setForm(p => ({ ...p, horaDesde: h, minutosDesde: m })); }} required />
                </Form.Group>

                {!editando ? (
                  <div className="col-md-12 mb-3">
                    <fieldset style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem' }}>
                      <legend style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6d1212', width: 'auto', padding: '0 0.5rem' }}>Días de la semana</legend>
                      <div className="d-flex gap-2 flex-wrap" role="group" aria-label="Días de la semana">
                        {DIAS.map(d => (
                          <button key={d.v} type="button"
                            aria-label={d.l} aria-pressed={form.dias.includes(d.v)}
                            onClick={() => toggleDia(d.v)}
                            style={{
                              padding: '0.4rem 0.9rem', borderRadius: '20px', border: '2px solid',
                              borderColor: form.dias.includes(d.v) ? '#6d1212' : '#dee2e6',
                              background: form.dias.includes(d.v) ? '#6d1212' : 'white',
                              color: form.dias.includes(d.v) ? 'white' : '#495057',
                              fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.15s'
                            }}>
                            {d.l}
                          </button>
                        ))}
                      </div>
                      {form.dias.length === 0 && <small style={{ color: '#dc3545', marginTop: '0.25rem', display: 'block' }}>Seleccioná al menos un día</small>}
                    </fieldset>
                  </div>
                ) : (
                  <Form.Group className="col-md-8 mb-3">
                    <Form.Label htmlFor="diaSemana-select">* Día de la semana</Form.Label>
                    <Form.Select id="diaSemana-select" aria-label="Día de la semana"
                      value={form.dias.length ? String(form.dias[0]) : ''}
                      onChange={e => setForm(p => ({ ...p, dias: e.target.value === '' ? [] : [Number(e.target.value)] }))} required>
                      <option value="">Seleccione...</option>
                      {DIAS_SEMANA.map((nombre, v) => <option key={v} value={v}>{nombre}</option>)}
                    </Form.Select>
                  </Form.Group>
                )}

                {form.tipoMedico === '2' && (
                  <div className="col-md-12">
                    <EstudiosCanalesHorario
                      idCentro={form.idCentro ? Number(form.idCentro) : null}
                      estudios={form.estudios}
                      onChange={(estudios) => setForm(p => ({ ...p, estudios }))}
                    />
                  </div>
                )}

                <Form.Group className="col-md-12 mb-3">
                  <Form.Label>Observación</Form.Label>
                  <Form.Control type="text" value={form.observacion} onChange={f('observacion')} />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !form.idMedico || !form.idCentro}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtros.idCentro ? 'filter-active' : ''}`}>
                <label className="filter-label">Centro</label>
                <select className="filter-input" value={filtros.idCentro}
                  onChange={e => setFiltros(p => ({ ...p, idCentro: e.target.value }))}>
                  <option value="">Todos</option>
                  {centros.map(c => <option key={c.idCentro} value={c.idCentro}>{c.centro}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={cargar} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setFiltros({ idCentro: '' }); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{horarios.length} horarios encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Horario
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('medico')} style={{ cursor: 'pointer' }}>
                    Médico / Técnico {getSortIcon('medico') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('centro')} style={{ cursor: 'pointer' }}>
                    Centro {getSortIcon('centro') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Tipo</th>
                  <th onClick={() => requestSort('diaSemana')} style={{ cursor: 'pointer' }}>
                    Día {getSortIcon('diaSemana') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('horaDesde')} style={{ cursor: 'pointer' }}>
                    Hora desde {getSortIcon('horaDesde') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Estudios por día</th>
                  <th>Observación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="8" className="empty-state">{loading ? 'Cargando...' : 'No hay horarios para mostrar'}</td></tr>
                ) : paginados.map(h => (
                  <tr key={h.idHorarioMedico}>
                    <td>{h.medico}</td>
                    <td>{h.centro}</td>
                    <td>{h.tipoMedico === 1 ? 'Médico' : 'Técnico'}</td>
                    <td>{h.diaSemana != null ? DIAS_SEMANA[h.diaSemana] : '—'}</td>
                    <td>{h.horaDesde || '—'}</td>
                    <td>{h.tipoMedico === 2 ? resumenCapacidad(h) : '—'}</td>
                    <td>{h.observacion}</td>
                    <td>
                      <RowActions onEdit={() => handleEditar(h)} onDelete={() => setConfirmEliminar(h.idHorarioMedico)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />
      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar horario"
        message="¿Estás seguro que querés eliminar este horario?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaHorariosMedicos;
